import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  Modal,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import closeicon from "../../../../../assets/images/close.svg";
import styles from "../../../../../styles/common/bulkupload.module.css";
import generateExcel from "../../../../../utils/excelFunctions";
import FileRead from "../../../../../Components/Common/FileRead";
import { get, post, postFile } from "../../../../../utils/api";
import BulkFileUpload from "../../../../Scope1/FugitiveEmmissions/BulkFileUpload";
import {
  buildInHouseBulkUploadReviewRows,
  getInHouseBulkUploadErrorMessage,
  stripInHouseBulkUploadInternalFields,
  filterInHouseRowsWithMonth,
  isInHouseMasterFieldResolved,
  isInHouseMasterFieldMatched,
  normalizeInHouseBulkUploadResponse,
  parseUploadedExcelFile,
  isOtherSpecified,
  OTHER_SPECIFIED,
  PROJECT_TYPE_OTHER_COL,
  METHODOLOGY_OTHER_COL,
  getInHouseFieldEnteredValue,
} from "./inHouseBulkUploadUtils";

const ADD_NEW_MASTER_VALUE = "add_new";

const compactFieldBoxSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  minWidth: "8rem",
  maxWidth: "26.5rem",
};

/** Keeps matched chip and edit controls in the same slot so the row does not jump. */
const reviewFieldActionSx = {
  ...compactFieldBoxSx,
  minHeight: 132,
  justifyContent: "flex-start",
};

const reviewFieldCellSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  verticalAlign: "top",
};

const reviewFieldErrorSlotSx = {
  minHeight: "2.25rem",
  display: "block",
};

const compactTextFieldSx = {
  "& .MuiInputBase-root": {
    fontSize: "0.8rem",
    minHeight: 32,
  },
  "& .MuiInputBase-input": {
    py: 0.5,
    px: 1,
  },
  "& fieldset": {
    transition: "none",
  },
};

const compactSelectSx = {
  fontSize: "0.8rem",
  "& .MuiSelect-select": {
    py: 0.5,
    minHeight: "32px !important",
  },
  "& fieldset": {
    transition: "none",
  },
};

function OtherSpecifiedField({
  index,
  fieldKey,
  otherColKey,
  options = [],
  row = {},
  enteredValue = "",
  matched = false,
  showInput = false,
  onSelectChange,
  onToggleInput,
  onCustomChange,
  onConfirmAddNew,
  allowAddNew = false,
  addNewLabel = "Add New Project Type +",
  addNewPending = false,
  error = "",
}) {
  const selected = String(row?.[fieldKey] ?? "").trim();
  const resolvedEntered = String(enteredValue ?? "").trim();
  const hasValidSelection =
    selected &&
    !isOtherSpecified(selected) &&
    selected !== ADD_NEW_MASTER_VALUE &&
    options.some(
      (opt) => String(opt).trim().toLowerCase() === selected.toLowerCase(),
    );
  const needsOther =
    !hasValidSelection &&
    (isOtherSpecified(resolvedEntered) ||
      isOtherSpecified(selected) ||
      selected === OTHER_SPECIFIED);

  const selectValue = selected;
  const safeSelectValue =
    !selectValue ||
    selectValue === ADD_NEW_MASTER_VALUE ||
    options.some(
      (opt) => String(opt).trim().toLowerCase() === selectValue.toLowerCase(),
    )
      ? selectValue
      : "";

  const showFieldError = Boolean(error) && !hasValidSelection;

  const renderSelect = () => (
    <FormControl size="small" fullWidth error={showFieldError}>
      <Select
        value={safeSelectValue}
        onChange={(e) =>
          onSelectChange(index, fieldKey, otherColKey, e.target.value)
        }
        displayEmpty
        sx={compactSelectSx}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 280 },
          },
        }}
      >
        <MenuItem value="">Tap to Select</MenuItem>
        {allowAddNew ? (
          <MenuItem value={ADD_NEW_MASTER_VALUE}>{addNewLabel}</MenuItem>
        ) : null}
        {options.map((opt) => (
          <MenuItem
            key={opt}
            value={opt}
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (matched) {
    return null;
  }

  if (needsOther) {
    const custom = String(row?.[otherColKey] ?? "").trim();

    if (showInput || custom) {
      return (
        <Box sx={compactFieldBoxSx}>
          {renderSelect()}
          <TextField
            size="small"
            fullWidth
            placeholder={`Enter ${fieldKey}`}
            value={row?.[otherColKey] || ""}
            onChange={(e) =>
              onCustomChange(index, fieldKey, otherColKey, e.target.value)
            }
            error={showFieldError}
            sx={compactTextFieldSx}
          />
          <Button
            type="button"
            variant="contained"
            size="small"
            disabled={!custom || addNewPending}
            onClick={() => onConfirmAddNew?.(index, fieldKey, otherColKey, custom)}
            sx={{
              alignSelf: "flex-start",
              fontSize: "0.75rem",
              py: 0.25,
              px: 1.25,
              minWidth: "48px",
              minHeight: "26px",
              lineHeight: 1.2,
              textTransform: "none",
            }}
          >
            Add
          </Button>
          {showFieldError ? (
            <Typography
              variant="caption"
              color="error"
              display="block"
              sx={reviewFieldErrorSlotSx}
            >
              {error}
            </Typography>
          ) : (
            <Box sx={reviewFieldErrorSlotSx} aria-hidden />
          )}
        </Box>
      );
    }

    return (
      <Box sx={compactFieldBoxSx}>
        {renderSelect()}
        <Button
          type="button"
          variant="contained"
          size="small"
          onClick={() => onToggleInput(index, fieldKey, true)}
          sx={{
            alignSelf: "flex-start",
            fontSize: "0.75rem",
            py: 0.25,
            px: 1.25,
            minWidth: "48px",
            minHeight: "26px",
            lineHeight: 1.2,
            textTransform: "none",
          }}
        >
          Add
        </Button>
        {showFieldError ? (
          <Typography variant="caption" color="error" sx={reviewFieldErrorSlotSx}>
            {error}
          </Typography>
        ) : (
          <Box sx={reviewFieldErrorSlotSx} aria-hidden />
        )}
      </Box>
    );
  }

  const isAddNewMode =
    allowAddNew &&
    (selectValue === ADD_NEW_MASTER_VALUE ||
      (showInput && !needsOther && !isOtherSpecified(selected)));

  if (isAddNewMode) {
    const draft = String(row?.[otherColKey] ?? "").trim();
    return (
      <Box sx={compactFieldBoxSx}>
        {renderSelect()}
        <TextField
          size="small"
          fullWidth
          placeholder={`Enter ${fieldKey}`}
          value={row?.[otherColKey] || ""}
          onChange={(e) =>
            onCustomChange(index, fieldKey, otherColKey, e.target.value)
          }
          error={showFieldError}
          sx={compactTextFieldSx}
        />
        <Button
          type="button"
          variant="contained"
          size="small"
          disabled={!draft || addNewPending}
          onClick={() => onConfirmAddNew?.(index, fieldKey, otherColKey, draft)}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Confirm
        </Button>
        {showFieldError ? (
          <Typography variant="caption" color="error" sx={reviewFieldErrorSlotSx}>
            {error}
          </Typography>
        ) : (
          <Box sx={reviewFieldErrorSlotSx} aria-hidden />
        )}
      </Box>
    );
  }

  return (
    <Box sx={compactFieldBoxSx}>
      {renderSelect()}
      {showFieldError ? (
        <Typography variant="caption" color="error" sx={reviewFieldErrorSlotSx}>
          {error}
        </Typography>
      ) : (
        <Box sx={reviewFieldErrorSlotSx} aria-hidden />
      )}
    </Box>
  );
}

const DEFAULT_METHODOLOGY_OPTIONS = [
  "Verra (VCS)",
  "Gold Standard",
  "CDM (Clean Development Mechanism)",
  "Plan Vivo",
  "ACR / CAR",
  "IPCC Guidelines",
  "MoEFCC / India National Protocols",
  "Internal / Company-defined (upload required)",
  OTHER_SPECIFIED,
];

const DEFAULT_PROJECT_TYPE_OPTIONS = [
  "Afforestation / Reforestation",
  "Agroforestry",
  "Biochar application",
  "Wetland / Peatland Restoration",
  "Mangrove Plantation",
  "Grassland Regeneration",
  "Soil Carbon Sequestration",
  "Ocean-based Carbon Sequestration",
  "Bamboo Plantation",
  OTHER_SPECIFIED,
];

const IN_HOUSE_MASTER_TYPES = {
  projectType: "projectType",
  methodologyUsed: "methodologyUsed",
};

const IN_HOUSE_MASTER_CONFIG = {
  [IN_HOUSE_MASTER_TYPES.projectType]: {
    fetchUrl: "/master/getAllInHouseProjectType",
    addUrl: "/master/addInHouseProjectType",
  },
  [IN_HOUSE_MASTER_TYPES.methodologyUsed]: {
    fetchUrl: "/master/getAllInHouseMethodologyUsed",
    addUrl: "/master/addInHouseMethodologyUsed",
  },
};

const mergeOptions = (defaults, apiValues = []) => {
  const seen = new Set(defaults);
  const rest = (apiValues || []).filter((v) => v && !seen.has(v));
  return [...defaults, ...rest];
};

const extractInHouseMasterNames = (data = []) =>
  [
    ...new Set(
      data
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.name ?? item?.projectType ?? item?.methodologyUsed ?? "",
        )
        .filter(Boolean),
    ),
  ];

const loadInHouseMasterOptions = async (
  baseOptions,
  defaultOptions,
  masterKind,
) => {
  const config = IN_HOUSE_MASTER_CONFIG[masterKind];
  if (!config?.fetchUrl) {
    return mergeOptions(
      baseOptions?.length ? baseOptions : defaultOptions,
      [],
    );
  }
  const response = await get(config.fetchUrl);
  const apiNames = extractInHouseMasterNames(response?.data?.data || []);
  return mergeOptions(
    baseOptions?.length ? baseOptions : defaultOptions,
    apiNames,
  );
};

const addInHouseMasterOption = async (name, masterKind) => {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) return null;
  const config = IN_HOUSE_MASTER_CONFIG[masterKind];
  if (!config?.addUrl) return trimmed;
  await post(config.addUrl, { name: trimmed });
  return trimmed;
};

const getRows = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return Object.values(data);
  return [];
};

const inputKey = (index, fieldKey) => `${index}-${fieldKey}`;

const matchedChipSx = {
  alignSelf: "flex-start",
  width: "fit-content",
  height: 22,
  fontSize: "0.7rem",
  "& .MuiChip-label": {
    px: 0.75,
    py: 0,
  },
};

const getEnteredDisplayValue = (item, row, fieldKey, otherColKey, keyValue) => {
  const entered = getInHouseFieldEnteredValue(item, row, keyValue, fieldKey);
  if (isOtherSpecified(entered)) {
    const otherName = String(row?.[otherColKey] ?? item?.[otherColKey] ?? "").trim();
    return otherName || OTHER_SPECIFIED;
  }
  return entered;
};

const BulkUpload = ({
  matchData,
  setMatchData,
  onClose,
  onConfirm,
  bulkUploadTable,
  title1 = "Methodology used",
  title2 = "Project Type",
  keyValue1 = "Methodology used",
  keyValue2 = "Project Type",
  options1 = [],
  options2 = [],
  matched1 = "matchedMethodologyUsed",
  matched2 = "matchedProjectType",
  setFormDataBulkUpload,
  formDataBulkUpload,
  bulkUploadUrl,
  setsnackOpen,
  jsonBulkUpload,
  initialFieldErrors = {},
  initialShowCustomInput = {},
  onReviewError,
}) => {
  const [methodologyOptions, setMethodologyOptions] = useState(
    () => options1?.length ? options1 : DEFAULT_METHODOLOGY_OPTIONS,
  );
  const [projectTypeOptions, setProjectTypeOptions] = useState(
    () => options2?.length ? options2 : DEFAULT_PROJECT_TYPE_OPTIONS,
  );
  const [showCustomInput, setShowCustomInput] = useState({});
  const [errors, setErrors] = useState(initialFieldErrors);
  const [finalJson, setFinalJson] = useState([]);
  const [addMasterPending, setAddMasterPending] = useState(null);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none");
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const options1Ref = useRef(options1);
  const options2Ref = useRef(options2);
  const masterOptionsFetchedRef = useRef(false);

  useEffect(() => {
    options1Ref.current = options1;
  }, [options1]);

  useEffect(() => {
    options2Ref.current = options2;
  }, [options2]);

  useEffect(() => {
    if (!bulkUploadTable) {
      masterOptionsFetchedRef.current = false;
    }
  }, [bulkUploadTable]);

  useEffect(() => {
    if (!bulkUploadTable) return;
    const initial = jsonBulkUpload?.length
      ? jsonBulkUpload
      : getRows(formDataBulkUpload);
    setFinalJson(Array.isArray(initial) ? [...initial] : []);
    setShowCustomInput(initialShowCustomInput || {});
  }, [bulkUploadTable, initialShowCustomInput]);

  useEffect(() => {
    if (!bulkUploadTable) return;
    if (Object.keys(initialFieldErrors || {}).length > 0) {
      setErrors(initialFieldErrors);
    }
  }, [bulkUploadTable, initialFieldErrors]);

  const refreshMethodologyOptions = useCallback(async () => {
    try {
      const merged = await loadInHouseMasterOptions(
        options1Ref.current,
        DEFAULT_METHODOLOGY_OPTIONS,
        IN_HOUSE_MASTER_TYPES.methodologyUsed,
      );
      setMethodologyOptions(merged);
      return merged;
    } catch (error) {
      console.error("Error fetching in-house methodologies:", error);
      const fallback = mergeOptions(
        options1Ref.current?.length
          ? options1Ref.current
          : DEFAULT_METHODOLOGY_OPTIONS,
        [],
      );
      setMethodologyOptions(fallback);
      return fallback;
    }
  }, []);

  const refreshProjectTypeOptions = useCallback(async () => {
    try {
      const merged = await loadInHouseMasterOptions(
        options2Ref.current,
        DEFAULT_PROJECT_TYPE_OPTIONS,
        IN_HOUSE_MASTER_TYPES.projectType,
      );
      setProjectTypeOptions(merged);
      return merged;
    } catch (error) {
      console.error("Error fetching in-house project types:", error);
      const fallback = mergeOptions(
        options2Ref.current?.length
          ? options2Ref.current
          : DEFAULT_PROJECT_TYPE_OPTIONS,
        [],
      );
      setProjectTypeOptions(fallback);
      return fallback;
    }
  }, []);

  useEffect(() => {
    if (!bulkUploadTable || masterOptionsFetchedRef.current) return;
    masterOptionsFetchedRef.current = true;

    const fetchMasterOptions = async () => {
      try {
        await Promise.all([
          refreshMethodologyOptions(),
          refreshProjectTypeOptions(),
        ]);
      } catch (error) {
        console.error("Error fetching in-house master values:", error);
      }
    };
    fetchMasterOptions();
  }, [bulkUploadTable, refreshMethodologyOptions, refreshProjectTypeOptions]);

  const isMethodologyResolved = useCallback(
    (item, row) =>
      isInHouseMasterFieldResolved(
        item,
        row,
        title1,
        METHODOLOGY_OTHER_COL,
        methodologyOptions,
        keyValue1,
      ),
    [title1, methodologyOptions, keyValue1],
  );

  const isMethodologyMatched = useCallback(
    (item, row) =>
      isInHouseMasterFieldMatched(
        item,
        row,
        title1,
        METHODOLOGY_OTHER_COL,
        methodologyOptions,
        keyValue1,
      ),
    [title1, methodologyOptions, keyValue1],
  );

  const isProjectTypeResolved = useCallback(
    (item, row) =>
      !title2 ||
      isInHouseMasterFieldResolved(
        item,
        row,
        title2,
        PROJECT_TYPE_OTHER_COL,
        projectTypeOptions,
        keyValue2,
      ),
    [title2, projectTypeOptions, keyValue2],
  );

  const isProjectTypeMatched = useCallback(
    (item, row) =>
      !title2 ||
      isInHouseMasterFieldMatched(
        item,
        row,
        title2,
        PROJECT_TYPE_OTHER_COL,
        projectTypeOptions,
        keyValue2,
      ),
    [title2, projectTypeOptions, keyValue2],
  );

  const effectiveMatchData = useMemo(() => {
    let rows = [];
    if (matchData?.length) rows = matchData;
    else if (jsonBulkUpload?.length) rows = jsonBulkUpload;
    else rows = getRows(formDataBulkUpload);
    return filterInHouseRowsWithMonth(rows);
  }, [matchData, jsonBulkUpload, formDataBulkUpload]);

  const reviewRows = useMemo(() => {
    const fromForm = getRows(
      Array.isArray(finalJson) && finalJson.length ? finalJson : formDataBulkUpload,
    );
    if (fromForm.length >= effectiveMatchData.length) return fromForm;
    return effectiveMatchData.map((item, index) => ({
      ...item,
      ...(fromForm[index] || {}),
    }));
  }, [finalJson, formDataBulkUpload, effectiveMatchData]);

  const confirmUploadDisabled = useMemo(() => {
    if (!effectiveMatchData?.length) return true;
    return !effectiveMatchData.every((item, index) => {
      const row = reviewRows[index] || {};
      return (
        isMethodologyResolved(item, row) &&
        isProjectTypeResolved(item, row)
      );
    });
  }, [
    effectiveMatchData,
    reviewRows,
    isMethodologyResolved,
    isProjectTypeResolved,
  ]);

  const updateRow = (index, updater) => {
    const applyUpdate = (prev) => {
      let rows = Array.isArray(prev) ? [...prev] : [...getRows(prev)];
      if (rows.length < effectiveMatchData.length) {
        rows = effectiveMatchData.map((item, i) => ({
          ...item,
          ...(rows[i] || {}),
        }));
      }
      const current = { ...(rows[index] || effectiveMatchData[index] || {}) };
      rows[index] = updater(current);
      return rows;
    };
    setFormDataBulkUpload(applyUpdate);
    setFinalJson(applyUpdate);
  };

  const handleSelectChange = (index, fieldKey, otherColKey, value) => {
    updateRow(index, (current) => ({
      ...current,
      [fieldKey]: value,
      ...(!isOtherSpecified(value) && value !== ADD_NEW_MASTER_VALUE
        ? { [otherColKey]: "" }
        : {}),
    }));
    if (value === ADD_NEW_MASTER_VALUE) {
      setShowCustomInput((prev) => ({ ...prev, [inputKey(index, fieldKey)]: true }));
    } else if (isOtherSpecified(value)) {
      setShowCustomInput((prev) => ({ ...prev, [inputKey(index, fieldKey)]: false }));
    } else {
      setShowCustomInput((prev) => {
        const next = { ...prev };
        delete next[inputKey(index, fieldKey)];
        return next;
      });
    }
    setErrors((prev) => {
      const next = { ...prev };
      if (next[index]) {
        delete next[index][fieldKey];
        delete next[index][otherColKey];
        if (!Object.keys(next[index]).length) delete next[index];
      }
      return next;
    });
  };

  const handleToggleInput = (index, fieldKey, visible) => {
    setShowCustomInput((prev) => ({ ...prev, [inputKey(index, fieldKey)]: visible }));
  };

  const handleCustomChange = (index, fieldKey, otherColKey, value) => {
    updateRow(index, (current) => ({
      ...current,
      [otherColKey]: value,
    }));
  };

  const handleConfirmAddMaster = async (
    index,
    fieldKey,
    otherColKey,
    name,
    masterType,
    refreshOptions,
    successMessage,
    errorMessage,
  ) => {
    const trimmed = String(name ?? "").trim();
    if (!trimmed) return;

    setAddMasterPending(masterType);
    try {
      await addInHouseMasterOption(trimmed, masterType);
      await refreshOptions();
      updateRow(index, (current) => ({
        ...current,
        [fieldKey]: trimmed,
        [otherColKey]: "",
      }));
      setShowCustomInput((prev) => {
        const next = { ...prev };
        delete next[inputKey(index, fieldKey)];
        return next;
      });
      setsnackOpen?.({
        open: true,
        message: successMessage,
        severity: "success",
      });
      setErrors((prev) => {
        const next = { ...prev };
        if (next[index]) {
          delete next[index][fieldKey];
          delete next[index][otherColKey];
          if (!Object.keys(next[index]).length) delete next[index];
        }
        return next;
      });
    } catch (err) {
      const message = err?.response?.data?.message || errorMessage;
      setsnackOpen?.({ open: true, message, severity: "error" });
    } finally {
      setAddMasterPending(null);
    }
  };

  const syncRowsWithDocuments = (updater) => {
    const applyUpdate = (prev) => {
      const rows = getRows(prev?.length ? prev : formDataBulkUpload);
      return Array.isArray(rows) ? updater(rows) : [];
    };
    setFormDataBulkUpload(applyUpdate);
    setFinalJson(applyUpdate);
  };

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      syncRowsWithDocuments((rows) =>
        rows.map((entry) => ({ ...entry, "Document (optional)": null })),
      );
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    syncRowsWithDocuments((rows) =>
      rows.map((entry) => ({ ...entry, "Document (optional)": null })),
    );
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    const filesArray = Array.isArray(files) ? files : Object.values(files || {});
    setCommonDocument(filesArray);
    syncRowsWithDocuments((rows) =>
      rows.map((entry) => ({ ...entry, "Document (optional)": filesArray })),
    );
    if (filesArray?.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleIndividualDocumentChange = (index, files) => {
    const filesArray = Array.isArray(files) ? files : Object.values(files || {});
    setIndividualDocuments((prev) => ({ ...prev, [index]: filesArray }));
    syncRowsWithDocuments((rows) =>
      rows.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": filesArray } : entry,
      ),
    );
    if (filesArray?.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleClearCommonDocument = () => {
    setCommonDocument(null);
    syncRowsWithDocuments((rows) =>
      rows.map((entry) => ({ ...entry, "Document (optional)": null })),
    );
  };

  const handleClearIndividualDocument = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    syncRowsWithDocuments((rows) =>
      rows.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": null } : entry,
      ),
    );
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed.",
      );
    }
    return validationErrors;
  };

  const performUpload = async () => {
    const dataToUse = getRows(finalJson?.length ? finalJson : formDataBulkUpload);
    const excelData = dataToUse.map((row) => {
      const cleaned = stripInHouseBulkUploadInternalFields(row);
      const methodology = String(
        cleaned?.["Methodology used"] ?? cleaned?.[title1] ?? "",
      ).trim();
      const methodologyOther = String(cleaned?.[METHODOLOGY_OTHER_COL] ?? "").trim();
      const projectType = String(cleaned?.["Project Type"] ?? cleaned?.[title2] ?? "").trim();
      const projectTypeOther = String(cleaned?.[PROJECT_TYPE_OTHER_COL] ?? "").trim();
      return {
        ...cleaned,
        "Methodology used":
          isOtherSpecified(methodology) && methodologyOther
            ? methodologyOther
            : methodology,
        "Project Type":
          isOtherSpecified(projectType) && projectTypeOther
            ? projectTypeOther
            : projectType,
      };
    });

    if (!bulkUploadUrl) {
      onConfirm?.();
      return;
    }

    try {
      const res = await post(bulkUploadUrl, { excelData });
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
      setsnackOpen?.({ open: true, message: res.data.message, severity: "success" });
      onConfirm?.();
      onClose?.();
    } catch (err) {
      const responseData = err?.response?.data || {};
      const status = err?.response?.status;
      if (status === 422 || status === 400) {
        let built = buildInHouseBulkUploadReviewRows(responseData, "statusList", excelData);
        if (built.rows.length > 0) {
          setMatchData?.(built.rows);
          setFormDataBulkUpload?.(built.rows);
          setFinalJson(built.rows);
          setErrors(built.fieldErrors || {});
          setShowCustomInput(built.autoShowInput || {});
          onReviewError?.({
            fieldErrors: built.fieldErrors,
            autoShowInput: built.autoShowInput,
          });
          return;
        }
      }
      const errMessage =
        responseData.details?.map((el) => el?.reason ?? el?.error).filter(Boolean).join(", ") ||
        getInHouseBulkUploadErrorMessage(responseData);
      setsnackOpen?.({ open: true, message: errMessage || "Network Error", severity: "error" });
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }

    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }

    await performUpload();
  };

  if (!bulkUploadTable) {
    return null;
  }

  if (!effectiveMatchData?.length) {
    return (
      <Dialog open={bulkUploadTable} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Could not load uploaded rows for review. Please close and upload
            the file again.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog
      open={bulkUploadTable}
      onClose={onClose}
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          width: "85%",
          margin: "16px",
          maxHeight: "calc(100% - 32px)",
          zIndex: 1000,
        },
      }}
    >
      <DialogTitle>Review Bulk Upload Data</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Review and resolve each field below its column heading. For &quot;Other
          specified&quot;, click Add to enter a custom value.
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>{title1}</TableCell>
                {title2 && (
                  <TableCell sx={{ fontWeight: "bold" }}>{title2}</TableCell>
                )}
                {documentUploadConfirmed && documentUploadType === "individual" && (
                  <TableCell sx={{ fontWeight: "bold" }}>Document</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {effectiveMatchData.map((item, index) => {
                const row = reviewRows[index] || {};
                const rowErrors = errors[index] || {};
                const methodologyMatched = isMethodologyMatched(item, row);
                const projectTypeMatched = isProjectTypeMatched(item, row);
                return (
                  <TableRow key={index}>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Box sx={reviewFieldCellSx}>
                        <Typography variant="body2" color="text.secondary">
                          {getEnteredDisplayValue(
                            item,
                            row,
                            title1,
                            METHODOLOGY_OTHER_COL,
                            keyValue1,
                          )}
                        </Typography>
                        <Box sx={reviewFieldActionSx}>
                          {methodologyMatched ? (
                            <Chip
                              label="Matched"
                              color="success"
                              size="small"
                              sx={matchedChipSx}
                            />
                          ) : (
                            <OtherSpecifiedField
                            index={index}
                            fieldKey={title1}
                            otherColKey={METHODOLOGY_OTHER_COL}
                            options={methodologyOptions}
                            row={row}
                            enteredValue={getInHouseFieldEnteredValue(
                              item,
                              row,
                              keyValue1,
                              title1,
                            )}
                            matched={false}
                            showInput={showCustomInput[inputKey(index, title1)]}
                            onSelectChange={handleSelectChange}
                            onToggleInput={handleToggleInput}
                            onCustomChange={handleCustomChange}
                            onConfirmAddNew={(rowIndex, fieldKey, otherColKey, name) =>
                              handleConfirmAddMaster(
                                rowIndex,
                                fieldKey,
                                otherColKey,
                                name,
                                IN_HOUSE_MASTER_TYPES.methodologyUsed,
                                refreshMethodologyOptions,
                                "Methodology added successfully",
                                "Methodology already exists or could not be created",
                              )
                            }
                            allowAddNew
                            addNewLabel="Add New Methodology +"
                            addNewPending={
                              addMasterPending === IN_HOUSE_MASTER_TYPES.methodologyUsed
                            }
                            error={rowErrors[METHODOLOGY_OTHER_COL]}
                          />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {title2 && (
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Box sx={reviewFieldCellSx}>
                          <Typography variant="body2" color="text.secondary">
                            {getEnteredDisplayValue(
                              item,
                              row,
                              title2,
                              PROJECT_TYPE_OTHER_COL,
                              keyValue2,
                            )}
                          </Typography>
                          <Box sx={reviewFieldActionSx}>
                            {projectTypeMatched ? (
                              <Chip
                                label="Matched"
                                color="success"
                                size="small"
                                sx={matchedChipSx}
                              />
                            ) : (
                              <OtherSpecifiedField
                              index={index}
                              fieldKey={title2}
                              otherColKey={PROJECT_TYPE_OTHER_COL}
                              options={projectTypeOptions}
                              row={row}
                              enteredValue={getInHouseFieldEnteredValue(
                                item,
                                row,
                                keyValue2,
                                title2,
                              )}
                              matched={false}
                              showInput={showCustomInput[inputKey(index, title2)]}
                              onSelectChange={handleSelectChange}
                              onToggleInput={handleToggleInput}
                              onCustomChange={handleCustomChange}
                              onConfirmAddNew={(rowIndex, fieldKey, otherColKey, name) =>
                                handleConfirmAddMaster(
                                  rowIndex,
                                  fieldKey,
                                  otherColKey,
                                  name,
                                  IN_HOUSE_MASTER_TYPES.projectType,
                                  refreshProjectTypeOptions,
                                  "Project type added successfully",
                                  "Project type already exists or could not be created",
                                )
                              }
                              allowAddNew
                              addNewLabel="Add New Project Type +"
                              addNewPending={
                                addMasterPending === IN_HOUSE_MASTER_TYPES.projectType
                              }
                              error={rowErrors[PROJECT_TYPE_OTHER_COL]}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    )}
                    {documentUploadConfirmed &&
                      documentUploadType === "individual" && (
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <BulkFileUpload
                            filesList={individualDocuments[index] || []}
                            handleChange={handleIndividualDocumentChange}
                            handleClear={handleClearIndividualDocument}
                            index={index}
                          />
                        </TableCell>
                      )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Document Upload
          </Typography>
          <Box>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                Do you want to upload document?
              </FormLabel>
              <RadioGroup
                value={documentUploadConfirmed ? "yes" : "no"}
                onChange={(e) =>
                  handleDocumentUploadConfirmation(e.target.value === "yes")
                }
                row
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>
          {documentUploadConfirmed && (
            <FormControl component="fieldset" sx={{ mb: 2, mt: 2 }}>
              <FormLabel component="legend">Document Upload Type</FormLabel>
              <RadioGroup
                value={documentUploadType}
                onChange={handleDocumentUploadTypeChange}
                row
              >
                <FormControlLabel
                  value="same"
                  control={<Radio />}
                  label="Same File for All Records"
                />
                <FormControlLabel
                  value="individual"
                  control={<Radio />}
                  label="Individual File for Each Record"
                />
              </RadioGroup>
            </FormControl>
          )}
          {documentUploadConfirmed && documentUploadType === "same" && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Upload a single document that will be associated with all
                records:
              </Typography>
              <BulkFileUpload
                filesList={commonDocument}
                handleChange={handleCommonDocumentChange}
                handleClear={handleClearCommonDocument}
              />
            </Box>
          )}
          {documentUploadConfirmed && documentUploadType === "individual" && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Upload individual documents for each record. Documents will be
                added as a new column in the table above.
              </Typography>
            </Box>
          )}
        </Box>

        {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#ffebee",
              borderRadius: 1,
              border: "1px solid #f44336",
            }}
          >
            <Typography
              variant="h6"
              color="error"
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              Error:
            </Typography>
            {uploadValidationErrors.map((error, index) => (
              <Typography
                key={index}
                variant="body2"
                color="error"
                sx={{ mb: 0.5 }}
              >
                • {error}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "1rem" }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmUpload}
          variant="contained"
          color="primary"
          disabled={confirmUploadDisabled}
        >
          Confirm Upload
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={showNoDocumentDialog}
      onClose={() => setShowNoDocumentDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">No Documents Uploaded</Typography>
          <IconButton
            onClick={() => setShowNoDocumentDialog(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You have selected to upload documents but haven&apos;t uploaded any
          files.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Do you want to proceed without uploading documents?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You can still upload documents later if needed.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: "1rem" }}>
        <Button
          onClick={() => setShowNoDocumentDialog(false)}
          variant="outlined"
          color="primary"
          sx={{ marginRight: "1rem" }}
        >
          Upload Documents
        </Button>
        <Button
          onClick={() => {
            setShowNoDocumentDialog(false);
            performUpload();
          }}
          variant="contained"
          color="primary"
        >
          Proceed Without Documents
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default BulkUpload;
export { BulkUpload as InHouseBulkUpload };

export function InHouseBulkUploadModal({
  onCloseBulkUpload,
  excelData,
  errorClose,
  setMatchData,
  setBulkUploadModal,
  isBulkUploadModal,
  fileName,
  url,
  setsnackOpen,
  matchedKey = "statusList",
  setFormDataBulkUpload,
  setJsonBulkUpload = null,
  fetchData,
}) {
  const [errors, setErrors] = useState(null);
  const [file, setFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const parsedFileRowsRef = useRef([]);

  const tryOpenBulkUploadReview = (
    responseData,
    fromError = false,
    fallbackExcelRows = [],
  ) => {
    const normalized = normalizeInHouseBulkUploadResponse(responseData);
    const fallbackRows = Array.isArray(fallbackExcelRows) ? fallbackExcelRows : [];

    let built =
      buildInHouseBulkUploadReviewRows(normalized, matchedKey, fallbackRows) || {};
    let matchRows = built.rows || [];

    const excelRows = Array.isArray(normalized.excelData)
      ? normalized.excelData
      : Array.isArray(normalized.finalData)
        ? normalized.finalData
        : [];

    if (
      matchRows.length === 0 &&
      (excelRows.length > 0 || fallbackRows.length > 0)
    ) {
      built = buildInHouseBulkUploadReviewRows(
        {
          ...normalized,
          excelData: excelRows.length ? excelRows : fallbackRows,
        },
        matchedKey,
        fallbackRows,
      );
      matchRows = built?.rows || excelRows || fallbackRows;
    }

    const hasDetailErrors =
      Array.isArray(normalized.details) && normalized.details.length > 0;

    const shouldOpenReview =
      matchRows.length > 0 ||
      normalized?.hasUnmatched ||
      excelRows.length > 0 ||
      fallbackRows.length > 0 ||
      (fromError && hasDetailErrors);

    if (!shouldOpenReview || !setMatchData) return false;

    let formRows =
      matchRows.length > 0
        ? matchRows
        : excelRows.length > 0
          ? excelRows
          : fallbackRows;

    if (
      !formRows.length &&
      fromError &&
      hasDetailErrors &&
      fallbackRows.length > 0
    ) {
      const retryBuilt = buildInHouseBulkUploadReviewRows(
        { ...normalized, excelData: fallbackRows },
        matchedKey,
        fallbackRows,
      );
      formRows = retryBuilt?.rows || fallbackRows;
      built = { ...built, ...retryBuilt };
    }

    if (!formRows.length && fromError && hasDetailErrors) {
      const detailBuilt = buildInHouseBulkUploadReviewRows(
        normalized,
        matchedKey,
        fallbackRows,
      );
      if (detailBuilt?.rows?.length) {
        formRows = detailBuilt.rows;
        built = { ...built, ...detailBuilt };
      }
    }

    if (!formRows.length) return false;

    const reviewMeta = {
      ...built,
      rows: formRows,
      warningMessage: fromError
        ? built.warningMessage ||
          normalized.error ||
          normalized.message ||
          ""
        : "",
    };

    setMatchData(formRows);
    setJsonBulkUpload && setJsonBulkUpload(formRows);
    setFormDataBulkUpload && setFormDataBulkUpload(formRows);
    setBulkUploadModal(false);
    errorClose && errorClose(fromError, normalized, reviewMeta);
    return true;
  };

  const handleDownload = async () => {
    const isMultiSheet =
      Array.isArray(excelData) &&
      excelData.length > 0 &&
      excelData[0]?.name &&
      excelData[0]?.data;
    const isEmpty = isMultiSheet
      ? excelData[0]?.data?.length === 0
      : excelData?.length === 0;

    if (isEmpty) {
      setsnackOpen({
        open: true,
        message: "Add masters data before uploading.",
        severity: "error",
      });
      return;
    }
    generateExcel(excelData, fileName);
  };

  const parseSelectedFile = async (selectedFile) => {
    if (!selectedFile) {
      parsedFileRowsRef.current = [];
      return [];
    }
    try {
      const parsedRows = await parseUploadedExcelFile(selectedFile);
      parsedFileRowsRef.current = Array.isArray(parsedRows) ? parsedRows : [];
      return parsedFileRowsRef.current;
    } catch (parseError) {
      console.error("Failed to parse uploaded in-house bulk file:", parseError);
      parsedFileRowsRef.current = [];
      return [];
    }
  };

  const handleFileChange = (newFile) => {
    setFile(newFile);
    if (newFile) {
      setErrors(null);
      parseSelectedFile(newFile);
    } else {
      parsedFileRowsRef.current = [];
    }
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);
    if (!file) {
      setErrors("Please select a file");
    } else if (!url) {
      return;
    } else {
      const parsedRows = await parseSelectedFile(file);

      await postFile(url, file)
        .then((res) => {
          const responseData = res?.data || {};
          const hasValidationIssue = Boolean(
            responseData?.error ||
              responseData?.hasUnmatched ||
              (Array.isArray(responseData?.details) &&
                responseData.details.length > 0),
          );
          if (
            tryOpenBulkUploadReview(
              responseData,
              hasValidationIssue,
              parsedRows,
            )
          ) {
            return;
          }
          setsnackOpen({
            open: true,
            message: responseData.message || "Uploaded successfully",
            severity: "success",
          });
          onCloseBulkUpload();
        })
        .catch((err) => {
          console.log("errw", err?.response);
          const responseData = err?.response?.data || {};
          const status = err?.response?.status;

          if (tryOpenBulkUploadReview(responseData, true, parsedRows)) {
            return;
          }

          if (status === 422 || status === 400) {
            setBulkUploadModal(false);
            setsnackOpen({
              open: true,
              message:
                responseData.error ||
                responseData.message ||
                (status === 422
                  ? "Bulk upload validation failed."
                  : "Network Error"),
              severity: "error",
            });
            return;
          }

          if (fetchData) fetchData();
        });
    }
  };

  useEffect(() => {
    if (!isSubmitted) return;
    if (file) {
      setErrors(null);
    } else {
      setErrors("Please select a file");
    }
  }, [file, isSubmitted]);

  return (
    <Modal
      open={isBulkUploadModal}
      onClose={onCloseBulkUpload}
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "stretch",
      }}
    >
      <Box
        sx={{
          width: "40%",
          height: "100vh",
          display: "flex",
        }}
      >
        <div
          style={{
            width: "30px",
            height: "30px",
            backgroundColor: "white",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0.75rem",
            cursor: "pointer",
          }}
          onClick={onCloseBulkUpload}
        >
          <img src={closeicon} alt="close" />
        </div>
        <div className={styles.upload_div}>
          <p className={styles.heading}>Upload Data</p>
          <div className={styles.table_div}>
            <div className={styles.table_sub_div}>
              <p className={styles.sub_heading}>Step 1 : </p>
            </div>
            <div className={styles.table_sub_div_two}>
              <p className={styles.sub_heading}>
                Download the{" "}
                <span style={{ color: "rgba(72, 130, 87, 1)" }}>.excel</span>{" "}
                File
              </p>
              <p className={styles.comment_heading}>
                Kindly download the excel Template to Continue Filling Data
              </p>
            </div>
          </div>
          <button className={styles.download_button} onClick={handleDownload}>
            Click Here To Download
          </button>
          <div className={styles.table_div}>
            <div className={styles.table_sub_div}>
              <p className={styles.sub_heading}>Step 2 : </p>
            </div>
            <div className={styles.table_sub_div_two}>
              <p className={styles.sub_heading}>
                Fill The Sheet Document & Save
              </p>
              <p className={styles.comment_heading}>
                Fill the downloaded excel sheet & save it on local storage
              </p>
            </div>
          </div>
          <div className={styles.table_div}>
            <div className={styles.table_sub_div}>
              <p className={styles.sub_heading}>Step 3 : </p>
            </div>
            <div className={styles.table_sub_div_two}>
              <p className={styles.sub_heading}>
                Upload the Filled{" "}
                <span style={{ color: "rgba(72, 130, 87, 1)" }}>.excel</span>{" "}
                File
              </p>
              <p className={styles.comment_heading}>
                Kindly Upload The Saved Excel file here
              </p>
            </div>
          </div>
          <FileRead name="file" setFile={handleFileChange} errors={errors} />
          <div className={styles.button_container}>
            <button className={styles.submit_button} onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
